/**
 * BridgeHealthModal — iter-259
 *
 * Roomy, real-time dashboard for the Desktop Bridge connection.
 * Surfaces the same info the BridgeBadge popover packs into ~280 px
 * but with breathing room + a live RTT graph + a "Test transport"
 * button that fires Play → wait → Stop so the user can verify their
 * DAW responded without leaving the studio.
 *
 * Panels:
 *   • Connection — state pill + uptime + session code.
 *   • Peer info  — bridge version, MIDI out port, MIDI in port,
 *                  platform (macOS / Windows / Linux). Driven by the
 *                  iter-258 `hello` payload via `onBridgeHello`.
 *   • RTT graph  — canvas sparkline of the last 60 ping samples
 *                  (≈30 s at one ping/500ms). Auto-scaled y-axis with
 *                  a clear 50 ms / 100 ms baseline overlay.
 *   • Test transport — fires Play → 600 ms wait → Stop. The user
 *                  visually confirms their DAW responded.
 *
 * The ping loop runs only while the modal is open so we don't waste
 * a WebSocket message every 500 ms when nobody's looking.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Activity, Cpu, Plug, RefreshCw, Wifi, WifiOff, X, ZapOff, Play, Square, Cable,
} from "lucide-react";
import { onBridgeHello, onBridgePong, sendPing, transport as bridgeTransport } from "@/lib/bridgeClient";

const PING_INTERVAL_MS = 500;
const HISTORY_LEN = 60;

function fmtUptime(ms) {
  if (ms <= 0) return "—";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m < 60) return `${m}m ${sec}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function rttColor(ms) {
  if (ms < 50) return "#22c55e";        // green — local network / loopback
  if (ms < 150) return "#f59e0b";       // amber — fine for transport
  return "#ef4444";                     // red — likely a sync problem
}

function rttLabel(ms) {
  if (ms < 50) return "EXCELLENT";
  if (ms < 150) return "GOOD";
  if (ms < 300) return "OK";
  return "POOR";
}

export default function BridgeHealthModal({ open, onClose, sessionCode, live }) {
  const [helloPayload, setHelloPayload] = useState(null);
  const [rttHistory, setRttHistory] = useState([]); // most recent last
  const [lastRtt, setLastRtt] = useState(null);
  const [uptimeStart, setUptimeStart] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [testState, setTestState] = useState("idle"); // idle | running | done
  const canvasRef = useRef(null);

  // Subscribe to the hello payload on mount — replays if we already saw one.
  useEffect(() => onBridgeHello(setHelloPayload), []);

  // Track connection uptime — reset whenever `live` flips on.
  useEffect(() => {
    if (live) setUptimeStart((s) => s || Date.now());
    else setUptimeStart(null);
  }, [live]);

  // Tick a clock so the uptime label re-renders every second.
  useEffect(() => {
    if (!open) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [open]);

  // Ping loop — only runs while the modal is open + connected.
  useEffect(() => {
    if (!open || !live) return undefined;
    const unsubPong = onBridgePong(({ rtt_ms }) => {
      setLastRtt(rtt_ms);
      setRttHistory((prev) => {
        const next = prev.length >= HISTORY_LEN ? prev.slice(-HISTORY_LEN + 1) : prev.slice();
        next.push(rtt_ms);
        return next;
      });
    });
    const pinger = setInterval(() => { sendPing(); }, PING_INTERVAL_MS);
    // Fire one immediately so the first sample lands within ~1 RTT
    // instead of waiting half a second.
    sendPing();
    return () => { clearInterval(pinger); unsubPong(); };
  }, [open, live]);

  // Reset history when the modal closes so re-opening doesn't show
  // stale data from a previous session.
  useEffect(() => {
    if (!open) {
      setRttHistory([]);
      setLastRtt(null);
      setTestState("idle");
    }
  }, [open]);

  // Draw the RTT sparkline.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof canvas.getContext !== "function") return;
    let ctx;
    // Guard so jsdom (no canvas implementation) + test environments
    // don't blow up — the modal still renders, just without the line.
    try { ctx = canvas.getContext("2d"); } catch (_e) { return; }
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;
    if (canvas.width !== cw * dpr) { canvas.width = cw * dpr; canvas.height = ch * dpr; }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cw, ch);

    // Auto-scaled y: top of the chart is max(150, observed_peak + 20).
    const peak = rttHistory.length ? Math.max(...rttHistory, 60) : 150;
    const yMax = Math.max(150, Math.ceil(peak / 50) * 50 + 20);

    // Baseline grid — 50, 100, 150 ms reference lines.
    ctx.strokeStyle = "rgba(255, 255, 255, 0.06)";
    ctx.lineWidth = 1;
    ctx.font = "10px ui-monospace, monospace";
    ctx.fillStyle = "rgba(255, 255, 255, 0.35)";
    for (const ref of [50, 100, 150]) {
      if (ref >= yMax) continue;
      const y = ch - (ref / yMax) * ch;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(cw, y);
      ctx.stroke();
      ctx.fillText(`${ref}ms`, 4, y - 2);
    }

    if (rttHistory.length < 2) return;
    // The line itself — colour the LAST segment by current RTT band
    // so the chart's tip reflects the current state at a glance.
    const stepX = cw / (HISTORY_LEN - 1);
    ctx.lineWidth = 2;
    for (let i = 1; i < rttHistory.length; i++) {
      const x0 = (i - 1) * stepX;
      const x1 = i * stepX;
      const y0 = ch - (rttHistory[i - 1] / yMax) * ch;
      const y1 = ch - (rttHistory[i] / yMax) * ch;
      ctx.strokeStyle = rttColor(rttHistory[i]);
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.stroke();
    }
    // Filled area under the curve — same gradient as the line, low opacity.
    ctx.fillStyle = "rgba(34, 197, 94, 0.12)";
    ctx.beginPath();
    ctx.moveTo(0, ch);
    for (let i = 0; i < rttHistory.length; i++) {
      const x = i * stepX;
      const y = ch - (rttHistory[i] / yMax) * ch;
      ctx.lineTo(x, y);
    }
    ctx.lineTo((rttHistory.length - 1) * stepX, ch);
    ctx.closePath();
    ctx.fill();
  }, [rttHistory]);

  const runTransportTest = async () => {
    if (testState === "running" || !live) return;
    setTestState("running");
    try {
      bridgeTransport.play(120);
      await new Promise((r) => setTimeout(r, 700));
      bridgeTransport.stop();
      setTestState("done");
      setTimeout(() => setTestState("idle"), 4000);
    } catch (e) {
      setTestState("idle");
    }
  };

  const avgRtt = useMemo(() => {
    if (rttHistory.length === 0) return null;
    const sum = rttHistory.reduce((a, b) => a + b, 0);
    return Math.round(sum / rttHistory.length);
  }, [rttHistory]);

  const uptimeMs = uptimeStart ? now - uptimeStart : 0;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="bridge-health-modal"
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/55 backdrop-blur-sm px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="w-full max-w-2xl overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#0c0d18] via-[#0f1020] to-[#0a0a14] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]"
            initial={{ scale: 0.92, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-emerald-300 drop-shadow-[0_0_10px_rgba(52,211,153,0.55)]" />
                <h2 className="font-display text-lg font-semibold tracking-tight">Bridge Health</h2>
                <span data-testid="bridge-health-state" className={`ml-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${
                  live ? "bg-emerald-500/15 text-emerald-200 ring-1 ring-emerald-300/40"
                       : "bg-rose-500/15 text-rose-200 ring-1 ring-rose-300/40"}`}>
                  {live ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                  {live ? "live" : "offline"}
                </span>
              </div>
              <button
                type="button"
                data-testid="bridge-health-close"
                onClick={onClose}
                className="rounded-full p-1.5 text-white/60 transition hover:bg-white/10 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body */}
            <div className="space-y-5 px-6 py-5">
              {/* Top row: 3 info cards */}
              <div className="grid grid-cols-3 gap-3">
                <StatCard
                  testid="bridge-health-uptime"
                  icon={<Plug className="h-3.5 w-3.5 text-emerald-300" />}
                  label="Uptime"
                  value={live ? fmtUptime(uptimeMs) : "—"}
                  caption={sessionCode ? `Session ${sessionCode}` : "no session"}
                />
                <StatCard
                  testid="bridge-health-rtt"
                  icon={<RefreshCw className="h-3.5 w-3.5 text-cyan-300" />}
                  label="Latency"
                  value={lastRtt != null ? `${lastRtt}ms` : "—"}
                  caption={avgRtt != null
                    ? `avg ${avgRtt}ms · ${rttLabel(avgRtt)}`
                    : "waiting…"}
                  valueColor={lastRtt != null ? rttColor(lastRtt) : undefined}
                />
                <StatCard
                  testid="bridge-health-version"
                  icon={<Cpu className="h-3.5 w-3.5 text-violet-300" />}
                  label="Bridge"
                  value={helloPayload?.version ? `v${helloPayload.version}` : "—"}
                  caption={helloPayload?.platform || "no peer connected"}
                />
              </div>

              {/* RTT graph */}
              <div>
                <div className="mb-1.5 flex items-center justify-between font-mono text-[10px] uppercase tracking-wider text-white/45">
                  <span>Ping round-trip · last {Math.min(rttHistory.length, HISTORY_LEN)} samples</span>
                  <span>{PING_INTERVAL_MS}ms interval</span>
                </div>
                <div className="rounded-2xl border border-white/10 bg-black/30 p-3">
                  <canvas
                    ref={canvasRef}
                    data-testid="bridge-health-rtt-canvas"
                    className="h-32 w-full"
                    aria-label="Bridge ping round-trip-time history"
                  />
                </div>
              </div>

              {/* MIDI ports row */}
              <div className="grid grid-cols-2 gap-3">
                <PortCard
                  testid="bridge-health-out-port"
                  icon={<Cable className="h-3.5 w-3.5 text-emerald-200" />}
                  label="MIDI Out → DAW"
                  port={helloPayload?.midi_out_port}
                  hint="ELPM → Ableton / Logic / FL Studio"
                />
                <PortCard
                  testid="bridge-health-in-port"
                  icon={<Cable className="h-3.5 w-3.5 text-amber-200" />}
                  label="MIDI In ← DAW"
                  port={helloPayload?.midi_in_port}
                  hint={helloPayload?.midi_in_port
                    ? "Reverse-sync active — DAW transport drives ELPM"
                    : "Add --input-port to the bridge to enable reverse sync"}
                />
              </div>

              {/* Test transport */}
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="mb-0.5 font-display text-sm font-semibold">Test transport</div>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-white/50">
                      Fires PLAY → wait 700 ms → STOP. Watch your DAW for the response.
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid="bridge-health-test-transport"
                    onClick={runTransportTest}
                    disabled={!live || testState === "running"}
                    className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 font-mono text-[11px] uppercase tracking-wider transition ${
                      testState === "done"
                        ? "bg-emerald-500/20 text-emerald-100 ring-1 ring-emerald-300/40"
                        : testState === "running"
                          ? "bg-cyan-500/20 text-cyan-100 ring-1 ring-cyan-300/40"
                          : "bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white hover:opacity-95 disabled:opacity-40"
                    }`}
                  >
                    {testState === "running"
                      ? <><Square className="h-3 w-3" /> Stopping…</>
                      : testState === "done"
                        ? <>Sent ✓</>
                        : <><Play className="h-3 w-3" /> Run test</>}
                  </button>
                </div>
              </div>

              {/* Offline hint */}
              {!live && (
                <div className="flex items-center gap-2 rounded-xl border border-amber-300/20 bg-amber-500/[0.06] px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-amber-200">
                  <ZapOff className="h-3.5 w-3.5" />
                  Bridge offline — click Connect in the Bridge popover to bring it online.
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function StatCard({ icon, label, value, caption, testid, valueColor }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-white/10 bg-white/[0.02] px-3 py-2.5">
      <div className="mb-1 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider text-white/45">
        {icon}{label}
      </div>
      <div className="font-display text-xl font-semibold" style={valueColor ? { color: valueColor } : undefined}>
        {value}
      </div>
      <div className="mt-0.5 truncate font-mono text-[9px] uppercase tracking-wider text-white/35">
        {caption}
      </div>
    </div>
  );
}

function PortCard({ icon, label, port, hint, testid }) {
  const has = !!port;
  return (
    <div data-testid={testid} className={`rounded-2xl border px-3 py-2.5 ${has ? "border-white/10 bg-white/[0.02]" : "border-white/5 bg-white/[0.01] opacity-70"}`}>
      <div className="mb-1 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider text-white/55">
        {icon}{label}
      </div>
      <div className="truncate font-display text-sm font-medium" style={!has ? { color: "rgba(255,255,255,0.4)" } : undefined}>
        {port || "not configured"}
      </div>
      <div className="mt-0.5 truncate font-mono text-[9px] uppercase tracking-wider text-white/35">
        {hint}
      </div>
    </div>
  );
}
